import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';
import type { NewsArticle } from '@tsuyosa/types';
import { useAuth } from '../context/AuthContext';
import { getAdminNews, deleteNews } from '../lib/api';
import { Spinner } from '@tsuyosa/ui';

export default function NewsPage() {
  const { accessToken } = useAuth();
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!accessToken) return;
    getAdminNews(accessToken)
      .then(({ data }) => setArticles(data))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load news'))
      .finally(() => setIsLoading(false));
  }, [accessToken]);

  async function handleDelete(id: string, title: string) {
    if (!accessToken) return;
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await deleteNews(accessToken, id);
      setArticles((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-100">News</h1>
          <p className="mt-0.5 text-sm text-gray-500">{articles.length} articles total</p>
        </div>
        <Link
          to="/news/new"
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
          style={{ backgroundColor: 'var(--tsuyosa-pink)' }}
        >
          <Plus size={16} />
          Create Article
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      ) : error ? (
        <p className="rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">{error}</p>
      ) : articles.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center">
          <p className="text-gray-500">No articles yet.</p>
          <Link to="/news/new" className="mt-2 inline-block text-sm text-pink-400 hover:underline">
            Create the first article
          </Link>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface)]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Title</th>
                <th className="hidden px-4 py-3 text-left text-xs font-medium text-gray-500 sm:table-cell">
                  Date
                </th>
                <th className="hidden px-4 py-3 text-left text-xs font-medium text-gray-500 md:table-cell">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {articles.map((article) => (
                <tr key={article.id} className="bg-[var(--color-surface)] hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {article.is_featured && (
                        <Star size={12} className="shrink-0 text-yellow-400" fill="currentColor" />
                      )}
                      <span className="font-medium text-gray-200 line-clamp-1">{article.title}</span>
                    </div>
                  </td>
                  <td className="hidden px-4 py-3 text-gray-400 sm:table-cell">
                    {article.date}
                  </td>
                  <td className="hidden px-4 py-3 md:table-cell">
                    {article.category && (
                      <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-gray-400">
                        {article.category}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        article.is_draft
                          ? 'bg-gray-500/15 text-gray-400'
                          : 'bg-green-500/15 text-green-400'
                      }`}
                    >
                      {article.is_draft ? 'Draft' : 'Published'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/news/${article.id}/edit`}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-white/5 hover:text-pink-400 transition-colors"
                      >
                        <Pencil size={14} />
                      </Link>
                      <button
                        onClick={() => handleDelete(article.id, article.title)}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-white/5 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
